import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { createPortal } from 'react-dom'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../auth/AuthContext.jsx'

const STORAGE_KEY = 'sofera:cookie_consent:v1'
const DEVICE_KEY = 'sofera:device_id'

export default function CookieConsent({ onOpenPrivacy, onOpenTerms }) {
  const [visible, setVisible] = useState(false)
  const [showPrefs, setShowPrefs] = useState(false)
  const [prefs, setPrefs] = useState({ necessary: true, analytics: false, marketing: false })
  const auth = useAuth()

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (!raw) {
        setVisible(true)
        return
      }
      const data = JSON.parse(raw)
      if (!data || typeof data !== 'object') {
        setVisible(true)
        return
      }
    } catch {
      setVisible(true)
    }
  }, [])

  const ensureDeviceId = () => {
    try {
      let id = localStorage.getItem(DEVICE_KEY)
      if (!id) {
        id = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `dev_${Date.now()}_${Math.random().toString(16).slice(2)}`
        localStorage.setItem(DEVICE_KEY, id)
      }
      return id
    } catch {
      return `dev_${Date.now()}`
    }
  }

  const persistServerSide = async (payload) => {
    try {
      const deviceId = ensureDeviceId()
      const userId = auth?.session?.user?.id || null
      const ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
      const jurisdiction = typeof navigator !== 'undefined' ? navigator.language : ''
      await supabase.from('cookie_consents').insert([{
        user_id: userId,
        device_id: deviceId,
        version: payload.version || 'v1',
        necessary: true,
        analytics: !!payload.analytics,
        marketing: !!payload.marketing,
        user_agent: ua,
        jurisdiction
      }])
    } catch {
      // registro de consentimento é best-effort no client; auditoria principal ocorrerá via service role
    }
  }

  const save = (next) => {
    try {
      const payload = { ...next, necessary: true, updatedAt: new Date().toISOString(), version: 'v1' }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
      try {
        const ev = new CustomEvent('cookie-consent:updated', { detail: payload })
        window.dispatchEvent(ev)
      } catch {}
      persistServerSide(payload)
    } catch {}
    setVisible(false)
    setShowPrefs(false)
  }

  const acceptAll = () => save({ analytics: true, marketing: true })
  const rejectAll = () => save({ analytics: false, marketing: false })
  const savePrefs = () => save(prefs)

  if (!visible) return null

  const content = (
    <>
      <div className="fixed inset-x-0 bottom-0 z-[210] p-4 md:p-6 pointer-events-none">
        <div className="pointer-events-auto bg-slate-900/95 border border-slate-800 rounded-2xl shadow-2xl max-w-4xl mx-auto">
          <div className="p-4 md:p-6">
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <h3 className="text-lg md:text-xl font-extrabold text-white">Usamos cookies</h3>
                <p className="text-slate-300 text-sm md:text-base mt-1">
                  Utilizamos cookies essenciais para o funcionamento da plataforma e, com seu consentimento, cookies de desempenho e marketing para melhorar sua experiência.
                </p>
                <div className="flex flex-wrap items-center gap-3 mt-3 text-sm">
                  <button
                    onClick={onOpenPrivacy}
                    className="text-teal-400 hover:text-teal-300 underline underline-offset-2"
                  >
                    Política de Privacidade
                  </button>
                  <span className="text-slate-600">•</span>
                  <button
                    onClick={onOpenTerms}
                    className="text-teal-400 hover:text-teal-300 underline underline-offset-2"
                  >
                    Termos de Uso
                  </button>
                </div>
              </div>
              <button
                onClick={() => setVisible(false)}
                className="text-slate-400 hover:text-white p-1 rounded"
                aria-label="Fechar"
              >
                <X size={18} />
              </button>
            </div>

            <div className="mt-4 flex flex-col sm:flex-row gap-3">
              <button
                onClick={acceptAll}
                className="flex-1 sm:flex-none px-5 py-2.5 rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-bold shadow-lg"
              >
                Aceitar todos
              </button>
              <button
                onClick={() => setShowPrefs(v => !v)}
                className="flex-1 sm:flex-none px-5 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-white font-bold"
              >
                Preferências
              </button>
              <button
                onClick={rejectAll}
                className="flex-1 sm:flex-none px-5 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-white font-bold"
              >
                Recusar não essenciais
              </button>
            </div>

            {showPrefs && (
              <div className="mt-4 border-t border-slate-800 pt-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="bg-slate-950/60 border border-slate-800 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-white font-bold text-sm">Essenciais</div>
                        <div className="text-slate-400 text-xs mt-1">Necessários para login, segurança e funcionalidades básicas.</div>
                      </div>
                      <span className="text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded bg-slate-800 text-slate-300">Sempre ativos</span>
                    </div>
                  </div>
                  <div className="bg-slate-950/60 border border-slate-800 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <label className="text-white font-bold text-sm" htmlFor="pref-analytics">Analíticos</label>
                        <div className="text-slate-400 text-xs mt-1">Ajudam a entender uso e melhorar a plataforma.</div>
                      </div>
                      <label className="inline-flex items-center cursor-pointer">
                        <input
                          id="pref-analytics"
                          type="checkbox"
                          checked={prefs.analytics}
                          onChange={e => setPrefs(p => ({ ...p, analytics: e.target.checked }))}
                          className="peer sr-only"
                        />
                        <span className="w-10 h-6 bg-slate-700 rounded-full relative after:content-[''] after:w-4 after:h-4 after:bg-white after:rounded-full after:absolute after:top-1 after:left-1 after:transition peer-checked:bg-teal-600 peer-checked:after:left-5"></span>
                      </label>
                    </div>
                  </div>
                  <div className="bg-slate-950/60 border border-slate-800 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <label className="text-white font-bold text-sm" htmlFor="pref-marketing">Marketing</label>
                        <div className="text-slate-400 text-xs mt-1">Personalização e campanhas externas.</div>
                      </div>
                      <label className="inline-flex items-center cursor-pointer">
                        <input
                          id="pref-marketing"
                          type="checkbox"
                          checked={prefs.marketing}
                          onChange={e => setPrefs(p => ({ ...p, marketing: e.target.checked }))}
                          className="peer sr-only"
                        />
                        <span className="w-10 h-6 bg-slate-700 rounded-full relative after:content-[''] after:w-4 after:h-4 after:bg-white after:rounded-full after:absolute after:top-1 after:left-1 after:transition peer-checked:bg-teal-600 peer-checked:after:left-5"></span>
                      </label>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-4">
                  <button
                    onClick={savePrefs}
                    className="px-5 py-2.5 rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-bold shadow-lg"
                  >
                    Salvar preferências
                  </button>
                  <button
                    onClick={() => setShowPrefs(false)}
                    className="px-5 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-white font-bold"
                  >
                    Fechar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )

  return createPortal(content, document.body)
}
